import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiPost } from '../../utils/api';
import { Button, Card, Spinner } from '../design-system';
import analyticsService from '../../services/analyticsService';
import PracticeSelection from '../practice/PracticeSelection';

const RoleSelection = ({ onRoleSelected }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPracticeSelection, setShowPracticeSelection] = useState(false);
  const { user, userInfo } = useAuth();
  const navigate = useNavigate();

  const handleRoleSelection = async (role) => {
    setIsLoading(true);
    setSelectedRole(role);

    try {
      // Register the user with their selected role
      const responseData = await apiPost('/.netlify/functions/register-user', {
        role: role,
        name: user.user_metadata?.full_name || user.email,
        email: user.email
      }, user);
      
      // Track role selection event (critical for onboarding funnel)
      try {
        // Get userId from response, userInfo context, or user object
        // Priority: responseData > userInfo > user.id (netlifyId fallback)
        const userId = responseData?.user?.userId || userInfo?.userId || user?.id;
        
        // Identify user with their selected role BEFORE tracking event
        if (userId) {
          analyticsService.identify(userId, {
            role: role
          });
          
          // Ensure identify() completes before tracking
          queueMicrotask(() => {
            analyticsService.track('role_selected', {
              userId: userId,
              selectedRole: role
            });
          });
        } else {
          // Fallback: track without userId (shouldn't happen but safe)
          analyticsService.track('role_selected', {
            selectedRole: role
          });
        }
      } catch (analyticsError) {
        // Don't break role selection if analytics fails
        console.error('Analytics tracking error:', analyticsError);
      }
      
      // If therapist, show practice selection; otherwise proceed
      if (role === 'therapist') {
        setShowPracticeSelection(true);
      } else {
        // Wait a moment for database to update, then trigger role check
        setTimeout(() => {
          if (onRoleSelected) {
            onRoleSelected();
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error registering user role:', error);
      setIsLoading(false);
    }
  };

  // Show practice selection if therapist role selected
  if (showPracticeSelection) {
    return (
      <PracticeSelection 
        onPracticeSelected={() => {
          // Wait a moment for database to update, then trigger role check
          setTimeout(() => {
            if (onRoleSelected) {
              onRoleSelected();
            }
          }, 500);
        }}
      />
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: 'var(--color-background)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: 'var(--space-8) var(--space-4)'
    }}>
      <div style={{ maxWidth: '448px', width: '100%', display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>
        <div>
          <h2 style={{ 
            marginTop: 'var(--space-6)', 
            textAlign: 'center', 
            fontSize: 'var(--text-h1)', 
            fontWeight: 'var(--weight-bold)', 
            color: 'var(--color-text)',
            fontFamily: 'var(--font-heading)',
            marginBottom: 'var(--space-2)'
          }}>
            Welcome to TheraEngage!
          </h2>
          <p style={{ 
            textAlign: 'center', 
            fontSize: 'var(--text-caption)', 
            color: 'var(--color-text)', 
            opacity: 0.8,
            margin: 0
          }}>
            Please select your role to get started
          </p>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Button
            variant="primary"
            size="large"
            onClick={() => handleRoleSelection('therapist')}
            disabled={isLoading}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}
          >
            {isLoading && selectedRole === 'therapist' ? (
              <Spinner size="small" />
            ) : null}
            I'm a Therapist
          </Button>

          <Button
            variant="success"
            size="large"
            onClick={() => handleRoleSelection('patient')}
            disabled={isLoading}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}
          >
            {isLoading && selectedRole === 'patient' ? (
              <Spinner size="small" />
            ) : null}
            I'm a Patient
          </Button>
        </div>

        <div>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', backgroundColor: 'rgba(0, 0, 0, 0.1)' }} />
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
              <span style={{ 
                padding: '0 var(--space-2)', 
                backgroundColor: 'var(--color-background)', 
                color: 'var(--color-text)', 
                opacity: 0.6,
                fontSize: 'var(--text-caption)'
              }}>
                What each role can do
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Card variant="default" padding="medium">
            <h3 style={{ 
              fontSize: 'var(--text-body)', 
              fontWeight: 'var(--weight-semibold)', 
              color: 'var(--color-text)',
              marginBottom: 'var(--space-2)',
              margin: 0
            }}>
              Therapist
            </h3>
            <ul style={{ 
              fontSize: 'var(--text-caption)', 
              color: 'var(--color-text)', 
              opacity: 0.8,
              margin: 0,
              paddingLeft: 'var(--space-4)',
              listStyle: 'none'
            }}>
              <li style={{ marginBottom: 'var(--space-1)' }}>• Manage your availability</li>
              <li style={{ marginBottom: 'var(--space-1)' }}>• View and manage bookings</li>
              <li style={{ marginBottom: 'var(--space-1)' }}>• Add and manage patients</li>
              <li style={{ marginBottom: 'var(--space-1)' }}>• Get booking notifications</li>
            </ul>
          </Card>

          <Card variant="default" padding="medium">
            <h3 style={{ 
              fontSize: 'var(--text-body)', 
              fontWeight: 'var(--weight-semibold)', 
              color: 'var(--color-text)',
              marginBottom: 'var(--space-2)',
              margin: 0
            }}>
              Patient
            </h3>
            <ul style={{ 
              fontSize: 'var(--text-caption)', 
              color: 'var(--color-text)', 
              opacity: 0.8,
              margin: 0,
              paddingLeft: 'var(--space-4)',
              listStyle: 'none'
            }}>
              <li style={{ marginBottom: 'var(--space-1)' }}>• Book appointments with therapists</li>
              <li style={{ marginBottom: 'var(--space-1)' }}>• View your upcoming appointments</li>
              <li style={{ marginBottom: 'var(--space-1)' }}>• Cancel or reschedule bookings</li>
              <li style={{ marginBottom: 'var(--space-1)' }}>• Receive booking confirmations</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RoleSelection; 